import { useCallback, useEffect, useRef, useState } from 'react';
import { api, errMsg, API_BASE } from '../lib/api';
import { getToken } from '../lib/auth';
import { Icon } from '../lib/icons';

interface MediaItem {
  id: string;
  type: 'photo' | 'video' | 'audio';
  caption: string | null;
  sizeBytes: number;
  url: string;
  createdAt: number;
}

interface Quota {
  photoTotal: number;
  photoRemaining: number;
  videoTotal: number;
  videoRemaining: number;
}

interface PackItem {
  id: string;
  name: string;
  description: string | null;
  priceCents: number;
  fallbackEmoji: string | null;
}

/** 纪念馆图片/视频上传与相册管理，含额度控制与扩容包购买 */
export default function MediaUploader({ memorialId }: { memorialId: string }) {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [quota, setQuota] = useState<Quota | null>(null);
  const [packs, setPacks] = useState<PackItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [quotaBlocked, setQuotaBlocked] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    const [mediaRes, prodRes] = await Promise.all([
      api<{ items: MediaItem[]; quota: Quota }>(`/media/memorial/${memorialId}`),
      api<{ groups: { categoryId: string; items: PackItem[] }[] }>('/products'),
    ]);

    if (mediaRes.ok && mediaRes.data) {
      setItems(mediaRes.data.items);
      setQuota(mediaRes.data.quota);
    }

    if (prodRes.ok && prodRes.data) {
      const packGroup = prodRes.data.groups.find((g) => g.categoryId === 'cat_pack');
      setPacks(packGroup?.items ?? []);
    }
  }, [memorialId]);

  useEffect(() => {
    load();
  }, [load]);

  async function upload(files: FileList | null) {
    if (!files || files.length === 0) return;

    setUploading(true);
    setError('');
    setQuotaBlocked(false);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setProgress(`正在上传 ${i + 1}/${files.length}：${file.name}`);

      try {
        const pres = await api<{
          mode: 'presigned' | 'direct';
          key: string;
          uploadUrl?: string;
          remaining?: number;
        }>('/media/presign', {
          method: 'POST',
          body: JSON.stringify({
            memorialId,
            filename: file.name,
            contentType: file.type,
            sizeBytes: file.size,
          }),
        });

        // 额度用完：停止上传并展示扩容包
        if (pres.error?.code === 'QUOTA_EXCEEDED') {
          setError(pres.error.message);
          setQuotaBlocked(true);
          break;
        }

        if (!pres.ok || !pres.data) {
          setError(errMsg(pres, '申请上传失败'));
          break;
        }

        const { mode, key, uploadUrl } = pres.data;

        if (mode === 'presigned' && uploadUrl) {
          const up = await fetch(uploadUrl, {
            method: 'PUT',
            body: file,
            headers: { 'Content-Type': file.type },
          });
          if (!up.ok) throw new Error(`上传失败 ${up.status}`);
        } else {
          const token = getToken();
          const up = await fetch(`${API_BASE}/media/upload?key=${encodeURIComponent(key)}`, {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': file.type,
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          });
          if (!up.ok) throw new Error(`上传失败 ${up.status}`);
        }

        const confirm = await api('/media/confirm', {
          method: 'POST',
          body: JSON.stringify({
            memorialId,
            key,
            contentType: file.type,
            sizeBytes: file.size,
          }),
        });
        if (!confirm.ok) throw new Error(errMsg(confirm, '保存失败'));
      } catch (e) {
        setError(e instanceof Error ? e.message : '上传失败');
        break;
      }
    }

    setUploading(false);
    setProgress('');
    if (inputRef.current) inputRef.current.value = '';
    load();
  }

  async function remove(id: string) {
    if (!confirm('确定删除这张照片/视频吗？')) return;
    await api(`/media/${id}`, { method: 'DELETE' });
    load();
  }

  /** 购买扩容包 */
  async function buyPack(packId: string) {
    const res = await api<{ orderId: string; payUrl: string }>('/orders', {
      method: 'POST',
      body: JSON.stringify({ memorialId, productId: packId, quantity: 1 }),
    });

    if (res.ok && res.data) {
      window.location.href = res.data.payUrl;
    } else {
      setError(errMsg(res, '下单失败，请先登录'));
    }
  }

  const photoUsed = quota ? quota.photoTotal - quota.photoRemaining : 0;
  const videoUsed = quota ? quota.videoTotal - quota.videoRemaining : 0;

  return (
    <div>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-ink-800">相册（{items.length}）</p>
          {quota && (
            <p className="mt-0.5 text-[11px] text-ink-500">
              照片 {photoUsed}/{quota.photoTotal} 张
              {quota.videoTotal > 0 && ` · 视频 ${videoUsed}/${quota.videoTotal} 段`}
            </p>
          )}
          <p className="mt-1 text-[11px] text-ink-400">
            支持 jpg / png / webp / gif / mp4 / webm
          </p>
        </div>

        <label className="cursor-pointer rounded-full bg-ink-900 px-5 py-2 text-xs text-ink-50">
          {uploading ? '上传中…' : '+ 上传'}
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/*,video/mp4,video/webm"
            className="hidden"
            disabled={uploading}
            onChange={(e) => upload(e.target.files)}
          />
        </label>
      </div>

      {progress && <p className="mb-3 text-xs text-candle-600">{progress}</p>}
      {error && (
        <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600">{error}</p>
      )}

      {/* 额度用尽时展示扩容包 */}
      {quotaBlocked && packs.length > 0 && (
        <div className="mb-4 rounded-xl border border-candle-400/40 bg-candle-300/10 p-4">
          <p className="mb-3 text-xs font-medium text-ink-800">购买扩容包</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {packs.map((p) => (
              <button
                key={p.id}
                onClick={() => buyPack(p.id)}
                className="flex items-center gap-3 rounded-xl border border-ink-200 bg-white p-3 text-left transition hover:border-candle-500"
              >
                <span className="text-2xl"><Icon name="box" /></span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-ink-900">{p.name}</span>
                  {p.description && (
                    <span className="mt-0.5 block truncate text-[11px] text-ink-500">
                      {p.description}
                    </span>
                  )}
                </span>
                <span className="shrink-0 text-sm font-medium text-candle-600">
                  ${(p.priceCents / 100).toFixed(2)}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-ink-300 py-10 text-center">
          <p className="text-3xl"><Icon name="image" /></p>
          <p className="mt-2 text-xs text-ink-500">还没有照片，上传亲人的影像</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
          {items.map((m) => (
            <div
              key={m.id}
              className="group relative aspect-square overflow-hidden rounded-xl border border-ink-200 bg-ink-100"
            >
              {m.type === 'video' ? (
                <video
                  src={m.url}
                  className="h-full w-full object-cover"
                  controls
                  preload="metadata"
                  playsInline
                />
              ) : (
                <img src={m.url} alt={m.caption ?? ''} className="h-full w-full object-cover" />
              )}

              {m.type === 'video' && (
                <span className="absolute left-2 top-2 rounded bg-black/60 px-1.5 py-0.5 text-[10px] text-white">
                  视频
                </span>
              )}

              <button
                onClick={() => remove(m.id)}
                className="absolute right-2 top-2 hidden h-7 w-7 items-center justify-center rounded-full bg-black/60 text-xs text-white group-hover:flex"
              >
                <Icon name="close" size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
